import { withDefault } from "collection-utils";
import type {
    ActionPropertyDescriptor,
    AppDescriptionContext,
    ColumnTypePredicate,
    ComponentBuilder,
    ComponentDescriptor,
    PropertyDescriptor,
    PropertyDescriptorCase,
    SchemaInspector,
} from ".";
import { isNamedPropertySource } from ".";
import type { DecodedArgumentSpec } from "../../common/components/deserialization";
import { decodeArgumentSpec } from "../../common/components/deserialization";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import type { UIClass } from "../../common/components/types";
import type { Doc } from "../../common/docUrl";
import { getDocURL } from "../../common/docUrl";
import { panic } from "../../common/Support";
import { makeScreenContextForActions } from "../actions";
import type {
    ActionComponentDescription,
    ActionKind,
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    TableGlideType,
} from "../../common/description";
import { getPrimitiveColumns, isStringTypeKind } from "../../common/description";
import { getPropertyAndDescriptorFromDescription } from "../description-utils";
import type { LetBinding } from "../serialized-component";
import { getActionsForComponent } from "./component-utils";
import { handlerForPropertyKind } from "./description-handlers";
import { ComponentHandlerBase } from "./handler";
import { makeDefaultActionDescriptorWithKinds } from "./primitive";
import { ColumnPropertyFlag, ColumnPropertyHandler } from "./property-handlers";
import type { PropertySection } from "./property-sections";

export interface PropertyDescriptorWithArgumentName {
    readonly descriptor: PropertyDescriptor | (() => PropertyDescriptor);
    readonly argumentName: string | undefined;
}

function getDescriptorForProperty({ descriptor }: PropertyDescriptorWithArgumentName): PropertyDescriptor {
    if (typeof descriptor === "function") {
        return descriptor();
    } else {
        return descriptor;
    }
}

function isPropertyDescriptorWithArgumentName(x: unknown): x is PropertyDescriptorWithArgumentName {
    if (typeof x !== "object" || x === null) return false;
    if (Object.keys(x).length !== 2) return false;
    const { descriptor, argumentName } = x as any;
    if (descriptor === undefined) return false;
    if (argumentName !== undefined && typeof argumentName !== "string") return false;
    return true;
}

interface SimpleComponentArguments {
    readonly kind?: ComponentKind;
    readonly name: string;
    readonly group: string;
    readonly description: string;
    readonly img?: string;
    readonly helpPath: Doc;
    readonly uiClass: UIClass;
}

function toLowerDashed(s: string): string {
    return s.toLowerCase().trim().replace(/\s+/g, "-");
}

export interface SimpleComponentDescription extends ActionComponentDescription {}

export abstract class SimpleComponentHandler extends ComponentHandlerBase<SimpleComponentDescription> {
    private readonly _name: string;
    private readonly _group: string;
    private readonly _description: string;
    private readonly _img: string;
    private readonly _helpPath: Doc;
    private readonly _elementName: string;
    private readonly _argsSpec: readonly DecodedArgumentSpec[];

    constructor({ kind, name, group, description, img, helpPath, uiClass }: SimpleComponentArguments) {
        const [maybeElementName, , ...argsSpec] = uiClass.spec;
        const elementName = withDefault(maybeElementName, name.trim().replace(/\s+/g, ""));

        if (kind === undefined) {
            kind = elementName.replace(/([^A-Z])([A-Z])/g, "$1-$2").toLowerCase();
        }

        super(kind);

        const nameLowerDashed = toLowerDashed(name);

        this._name = name;
        this._group = group;
        this._description = description;

        this._img = withDefault(img, nameLowerDashed);
        this._helpPath = helpPath;
        this._elementName = elementName;

        this._argsSpec = argsSpec.map(decodeArgumentSpec);
    }

    protected get configuresScreenTitle(): boolean {
        return false;
    }

    protected abstract getPropertyDescriptors(
        desc: SimpleComponentDescription | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined,
        schema: SchemaInspector | undefined,
        table: TableGlideType | undefined
    ): readonly (PropertyDescriptor | PropertyDescriptorWithArgumentName)[];

    protected getActionKinds?(
        mutatingScreenKind: MutatingScreenKind | undefined,
        appHasUserProfile: boolean
    ): readonly ActionKind[];

    protected getIsLegacy(): boolean {
        return false;
    }

    private getProperties(
        desc: SimpleComponentDescription | undefined,
        table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly PropertyDescriptorWithArgumentName[] {
        return this.getPropertyDescriptors(desc, mutatingScreenKind, schema, table).map(p => {
            if (isPropertyDescriptorWithArgumentName(p)) return p;
            if (!isNamedPropertySource(p.property)) {
                return panic("Simple component handler only supports named property sources");
            }
            return { descriptor: p, argumentName: p.property.name };
        });
    }

    public getDescriptor(
        desc: SimpleComponentDescription | undefined,
        table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const properties = this.getProperties(desc, table, ccc, mutatingScreenKind);

        return {
            name: this._name,
            description: this._description,
            img: this._img,
            group: this._group,
            helpUrl: getDocURL(this._helpPath),
            isLegacy: this.getIsLegacy(),
            properties: [...properties.map(getDescriptorForProperty), ...this.getBasePropertyDescriptors()],
            configuresScreenTitle: this.configuresScreenTitle,
        };
    }

    public getActionDescriptors(
        _desc: SimpleComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor[] {
        const actionKinds = this.getActionKinds?.(mutatingScreenKind, schema?.userProfileTableInfo !== undefined);
        if (actionKinds === undefined) return [];
        return [makeDefaultActionDescriptorWithKinds(actionKinds, false)];
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: SimpleComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const args: { [key: string]: SerializedComponent | undefined } = {};

        for (const property of this.getProperties(desc, tables.input, builder, mutatingScreenKind)) {
            const { argumentName } = property;
            if (argumentName === undefined) continue;

            const descriptor = getDescriptorForProperty(property);

            const argSpec = this._argsSpec.find(s => s.argName === argumentName);
            if (argSpec === undefined) {
                return panic(`Argument ${argumentName} not found`);
            }

            let argument: SerializedComponent | undefined;

            const propertyAndDescriptor = getPropertyAndDescriptorFromDescription(desc, descriptor);
            if (propertyAndDescriptor !== undefined) {
                const { description, descriptorCase } = propertyAndDescriptor;
                const handler = handlerForPropertyKind(descriptorCase.kind);
                argument = handler.makeArgumentForComponent(
                    builder,
                    tables.input,
                    description,
                    descriptorCase as PropertyDescriptorCase
                );
            }

            if (argument === undefined) {
                if (argSpec.required) return b.emptyUI;
                continue;
            }

            args[argumentName] = argument;
        }

        const lets: LetBinding[] = [];
        if (this.getActionKinds?.(mutatingScreenKind, builder.userProfileTableInfo !== undefined) !== undefined) {
            const { actions } = getActionsForComponent(this, desc, tables.input, builder, mutatingScreenKind);
            const actionsContext = makeScreenContextForActions(builder, actions, tables, mutatingScreenKind);
            lets.push(...actionsContext.lets);
            const onTap = builder.makeSequenceFromActions(
                actions,
                tables,
                actionsContext.screenContext,
                outputContext,
                mutatingScreenKind,
                false
            );
            args["onTap"] = onTap;
        }

        return b.let(lets, b.ui(this._elementName, args));
    }
}

interface SimplePrimitiveColumnPropertyArguments {
    readonly name: string;
    readonly label: string;
    readonly preferredType: ColumnTypePredicate;
    readonly isRequired?: boolean;
    readonly searchable?: boolean;
    readonly preferredNames?: readonly string[];
    readonly mustBeString?: boolean;
    readonly section: PropertySection;
}

export class SimplePrimitiveColumnPropertyHandler extends ColumnPropertyHandler {
    constructor({
        name,
        label,
        preferredType,
        isRequired,
        searchable,
        preferredNames,
        mustBeString,
        section,
    }: SimplePrimitiveColumnPropertyArguments) {
        const flags = [ColumnPropertyFlag.Editable];
        if (withDefault(isRequired, true)) {
            flags.push(ColumnPropertyFlag.Required);
        }
        if (withDefault(searchable, false)) {
            flags.push(ColumnPropertyFlag.Searchable);
        }

        const getAllowedColumns =
            mustBeString === true
                ? (t: TableGlideType) => t.columns.filter(c => isStringTypeKind(c.type.kind))
                : getPrimitiveColumns;

        super(name, label, flags, undefined, preferredNames, getAllowedColumns, preferredType, section);
    }
}
